import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { trainingBlockApi, trainingProgramApi } from '../../api'
import { flattenTrainingTags } from '../../api/trainingBlockApi'
import { subscriptionApi } from '../../api/subscriptionApi'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import { workoutApi } from '../../api/workoutApi'
import type { WorkoutSession } from '../../api/workoutApi'
import type { ApiError } from '../../api/client'
import type { TrainingBlock, TrainingProgram, TrainingProgramActivation } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n'
import { Button, Card, EmptyState, Loading } from '../ui'
import { WORKOUT_FINISHED_EVENT } from '../../utils/workoutEvents'
import { TrainingBlockCard } from './TrainingBlockCard'
import ActiveWorkoutWidget from './ActiveWorkoutWidget'
import { captureEvent } from '../../posthog'

export default function WorkoutStart() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const entitlements = entitlementsOf(user)
  const canUseWorkoutMode = entitlements.workoutMode
  const isFreeSelfTrainer = !entitlements.hasActiveCoach && !canUseWorkoutMode
  const trialAvailable = Boolean(user?.trialAvailable)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savedBlocks, setSavedBlocks] = useState<TrainingBlock[]>([])
  const [currentProgram, setCurrentProgram] = useState<TrainingProgram | null>(null)
  const [tagEmojiMap, setTagEmojiMap] = useState<Record<string, string>>({})
  const [tagLabelMap, setTagLabelMap] = useState<Record<string, string>>({})
  const [startingBlockId, setStartingBlockId] = useState<number | null>(null)
  const [activeSession, setActiveSession] = useState<WorkoutSession | null>(null)
  const [trialBusy, setTrialBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    let redirected = false
    try {
      const [history, active, tagsData] = await Promise.all([
        trainingProgramActivationApi.getHistory().catch(() => [] as TrainingProgramActivation[]),
        workoutApi.getActiveSession().catch(() => null),
        trainingBlockApi.getTags().catch(() => null),
      ])
      setActiveSession(active)
      if (tagsData) {
        const tags = flattenTrainingTags(tagsData)
        setTagEmojiMap(Object.fromEntries(tags.map(tag => [tag.key, tag.emoji])))
        setTagLabelMap(Object.fromEntries(tags.map(tag => [tag.key, tag.label])))
      }
      const current = history.find(a => a.isActive) ?? null
      if (current && !isFreeSelfTrainer) {
        redirected = true
        navigate(`/training-programs/${current.programId}`, { replace: true })
        return
      }

      if (isFreeSelfTrainer) {
        setSavedBlocks([])
        if (current) {
          const program = await trainingProgramApi.getProgram(current.programId)
          setCurrentProgram(program)
        } else {
          setCurrentProgram(null)
        }
        return
      }

      setCurrentProgram(null)
      const blocks = await trainingBlockApi.getBlocks()
      setSavedBlocks(blocks)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.loadFailed)
    } finally {
      if (!redirected) setLoading(false)
    }
  }, [isFreeSelfTrainer, navigate, t.workout.error.loadFailed])

  useEffect(() => {
    void load()
  }, [load, location.pathname])

  useEffect(() => {
    const refresh = () => {
      workoutApi.getActiveSession()
        .then((active) => setActiveSession(active))
        .catch(() => setActiveSession(null))
    }
    window.addEventListener(WORKOUT_FINISHED_EVENT, refresh)
    return () => window.removeEventListener(WORKOUT_FINISHED_EVENT, refresh)
  }, [])

  async function startBlock(blockId: number) {
    if (activeSession?.id != null) {
      navigate(`/training/session/${activeSession.id}`, { replace: true })
      return
    }
    if (!canUseWorkoutMode) {
      navigate('/subscription')
      return
    }
    setStartingBlockId(blockId)
    setError(null)
    try {
      const session = await workoutApi.startSession(blockId, null)
      captureEvent('workout_started', { session_id: session.id, block_id: blockId, source: 'workout_start' })
      navigate(`/training/session/${session.id}`, { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 403 || apiError.message?.includes('subscription.workout.mode.required')) {
        navigate('/subscription')
      } else {
        setError(apiError.message || t.workout.error.startFailed)
      }
    } finally {
      setStartingBlockId(null)
    }
  }

  async function startTrial() {
    try {
      setTrialBusy(true)
      await subscriptionApi.activateTrial()
      captureEvent('trial_activated', { source: 'workout_start' })
      await refreshUser()
      navigate('/subscription', { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.subscription.trial.activateFailed)
    } finally {
      setTrialBusy(false)
    }
  }

  const canStart = !isFreeSelfTrainer && activeSession?.id == null

  return (
    <div className="px-4 py-4 pb-24">
      <h1 className="text-xl font-bold mb-1" style={{ color: theme.text.primary }}>
        {isFreeSelfTrainer ? t.workout.freePlan.title : t.workout.startTitle}
      </h1>
      <p className="text-sm mb-5" style={{ color: theme.text.muted }}>
        {isFreeSelfTrainer ? t.workout.freePlan.subtitle : t.workout.startSubtitle}
      </p>

      {loading && <Loading message={t.workout.loading} />}

      {error && (
        <div
          className="rounded-xl p-3 mb-4 text-sm"
          style={{ backgroundColor: 'rgba(239,68,68,0.12)', color: theme.text.primary }}
        >
          {error}
        </div>
      )}

      {!loading && activeSession?.id != null && (
        <ActiveWorkoutWidget workout={activeSession} />
      )}

      {!loading && isFreeSelfTrainer && (
        <FreePlanProgramView
          program={currentProgram}
          tagEmojiMap={tagEmojiMap}
          tagLabelMap={tagLabelMap}
          onUpgrade={() => navigate('/subscription')}
          onGoToPrograms={() => navigate('/training-programs')}
        />
      )}

      {!loading && canStart && (
        <section>
          <h2 className="text-sm font-bold mb-3" style={{ color: theme.text.primary }}>
            {t.workout.chooseSavedBlock}
          </h2>
          {savedBlocks.length === 0 ? (
            <EmptyState
              icon="🏋️"
              message={t.workout.noSavedBlocks}
              action={{
                label: t.workout.goToBlocks,
                onClick: () => navigate('/training-blocks'),
              }}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {savedBlocks.map((block) => (
                <TrainingBlockCard
                  key={block.id}
                  name={block.name}
                  items={block.items}
                  tags={block.tags}
                  tagEmojiMap={tagEmojiMap}
                  tagLabelMap={tagLabelMap}
                  onClick={() => startBlock(block.id)}
                  footer={
                    <div className="flex items-center justify-end w-full">
                      <Button
                        type="button"
                        variant="brand"
                        size="sm"
                        disabled={startingBlockId === block.id}
                        onClick={() => startBlock(block.id)}
                      >
                        {startingBlockId === block.id ? t.workout.loading : t.workout.startBlock}
                      </Button>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </section>
      )}

      {!loading && !canUseWorkoutMode && !isFreeSelfTrainer && (
        <div className="mt-6">
          {trialAvailable && (
            <Button
              fullWidth
              variant="brand"
              disabled={trialBusy}
              className="mb-3"
              onClick={() => void startTrial()}
            >
              {trialBusy ? t.common.loading : t.subscription.trial.activate}
            </Button>
          )}
          <Button fullWidth onClick={() => navigate('/subscription')}>
            {t.subscription.unlockCta}
          </Button>
        </div>
      )}
    </div>
  )
}

function FreePlanProgramView({
  program,
  tagEmojiMap,
  tagLabelMap,
  onUpgrade,
  onGoToPrograms,
}: {
  program: TrainingProgram | null
  tagEmojiMap: Record<string, string>
  tagLabelMap: Record<string, string>
  onUpgrade: () => void
  onGoToPrograms: () => void
}) {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-5">
      <Card noHover>
        <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
          {t.workout.freePlan.bannerTitle}
        </p>
        <p className="text-sm leading-relaxed" style={{ color: theme.text.secondary }}>
          {t.workout.freePlan.bannerBody}
        </p>
        <Button type="button" variant="primary" size="md" fullWidth className="mt-3" onClick={onUpgrade}>
          {t.workout.freePlan.upgradeCta}
        </Button>
      </Card>

      {program == null ? (
        <EmptyState
          icon="🗂️"
          message={t.workout.freePlan.noProgram}
          action={{
            label: t.workout.freePlan.goToPrograms,
            onClick: onGoToPrograms,
          }}
        />
      ) : (
        <section>
          <h2 className="text-sm font-bold mb-1" style={{ color: theme.text.primary }}>
            {t.workout.activeProgram}
          </h2>
          <p className="text-base font-semibold mb-3" style={{ color: theme.text.primary }}>
            {program.name}
          </p>
          {program.days.length === 0 ? (
            <p className="text-sm" style={{ color: theme.text.muted }}>
              {t.workout.noBlocksInProgram}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {program.days.map(day => (
                <TrainingBlockCard
                  key={day.id}
                  name={day.blockName ?? `Block #${day.blockId}`}
                  items={day.items ?? []}
                  tags={day.blockTags ?? []}
                  tagEmojiMap={tagEmojiMap}
                  tagLabelMap={tagLabelMap}
                  index={day.sortOrder + 1}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
